import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDate, t } from '@aquabox/shared';
import { api } from '../api/client';
import {
  fileToBase64,
  useStockIntakeParse,
  useStockIntakeReceive,
  type StockIntakeLine,
} from '../api/stock-intake';
import type { Page, Product } from '../api/types';

/**
 * "Belgeden ekle" — fotoğraf/PDF ya da Excel yapıştırma taslağı üretir (görev tanımı).
 *
 * ÇOK KALEMLİ İRSALİYE (S7): Taslak deftere YAZILMAZ. Kullanıcı kalemleri (ürün + adet) ve
 * irsaliye numarasını düzeltir, "Kaydet" TEK çağrıyla `POST /api/stock/shipments/receive`
 * ucuna gider. Sunucu kalemleri TEK transaction'da yazar: bir kalem geçersizse HİÇBİRİ
 * yazılmaz. Satır satır `POST /api/stock/shipments` çağrısı YAPILMAZ — irsaliye numarası
 * kalemler arasında ortaktır, satır satır gönderim aynı irsaliyeyi ikinci kez yazmaya
 * çalışır ve 409 alır.
 *
 * Bu ekran YALNIZ "ne yazılacak" bilgisini toplar: adet toplamı, stok hesabı ve parasal
 * işlem PANELDE YAPILMAZ (kural 4 — hesap backend'de).
 */

type Tab = 'photo' | 'paste';

interface DraftLine extends StockIntakeLine {
  /**
   * K1 (hakem RET): satır dizininden DEĞİL, okuma anında üretilen sabit kimlik. `clientId` bu
   * anahtardan türetilir (`intake-<uuid>`); satır silinince kalan satırların kimliği KAYMAZ.
   */
  lineKey: string;
  status: 'idle' | 'pending' | 'done' | 'error';
  /** U3: sunucunun döndürdüğü hata metni — genel bir "işlem başarısız" değil, satırın altında. */
  error?: string;
  /**
   * U5: düşük güvenli satır kullanıcı onu DÜZENLEMEDEN ya da açıkça "Doğru" DEMEDEN
   * kaydedilemez. Herhangi bir alan değişikliği ya da onay checkbox'ı bu bayrağı `true` yapar.
   */
  touched: boolean;
}

const ACCEPTED_TYPES = 'image/jpeg,image/png,image/webp,application/pdf';

export function StockIntakeModal({ onDone }: { onDone: () => void }) {
  const [tab, setTab] = useState<Tab>('photo');
  const [pasteText, setPasteText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [lines, setLines] = useState<DraftLine[] | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  /** İrsaliye numarası BELGE seviyesindedir: kalemlerin hepsi aynı irsaliyeye girer. */
  const [waybillNo, setWaybillNo] = useState('');
  /** Belge tarihi — `note` alanına yazılır (sunucu metni değiştirmez). */
  const [docDate, setDocDate] = useState('');
  const [savingLoop, setSavingLoop] = useState(false);
  const savingRef = useRef(false);
  const linesRef = useRef<DraftLine[] | null>(lines);
  useEffect(() => {
    linesRef.current = lines;
  }, [lines]);
  // Kayıt sürerken alanlar kilitlidir; yine de gönderim anındaki DEĞER okunur (ref).
  const waybillRef = useRef(waybillNo);
  useEffect(() => {
    waybillRef.current = waybillNo;
  }, [waybillNo]);
  const docDateRef = useRef(docDate);
  useEffect(() => {
    docDateRef.current = docDate;
  }, [docDate]);
  /**
   * İstek anahtarı: BELGE başına bir kez üretilir ve aynı belgenin tekrar denemelerinde SABİT
   * kalır. Sunucu aynı `clientId`yi ikinci kez görürse yeni satır YAZMAZ (idempotency).
   */
  const clientIdRef = useRef<string>('');
  // Pencere kapanırsa kayıt arka planda yazmaya devam etmesin. Gövde de `false` yazar: StrictMode
  // geliştirmede efekti iki kez çalıştırır.
  const unmountedRef = useRef(false);
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  const parse = useStockIntakeParse();
  const receive = useStockIntakeReceive();
  const qc = useQueryClient();
  const products = useQuery({
    queryKey: ['products', 'includeInactive'],
    queryFn: () => api.get<Page<Product>>('/api/products?includeInactive=true'),
  });
  const productList = products.data?.items ?? [];

  async function read(): Promise<void> {
    if (docLocked) return; // K1a: düğme kapalı; bu ikinci emniyet.
    const input =
      tab === 'paste'
        ? { text: pasteText }
        : file
          ? { file: { base64: await fileToBase64(file), mime: file.type, filename: file.name } }
          : null;
    if (!input) return;
    const result = await parse.mutateAsync(input);
    setLines(
      result.lines.map((l) => ({
        ...l,
        lineKey: crypto.randomUUID(),
        status: 'idle' as const,
        touched: false,
      })),
    );
    setWarnings(result.warnings);
    // İrsaliye numarası ve tarih BELGE seviyesindedir: ilk bulunan değer önerilir.
    const waybills = [
      ...new Set(result.lines.map((l) => l.waybillNo?.trim()).filter((w): w is string => !!w)),
    ];
    setWaybillNo(waybills[0] ?? '');
    setDocDate(result.lines.find((l) => l.date)?.date ?? '');
    clientIdRef.current = `intake-${crypto.randomUUID()}`;
  }

  /**
   * Yalnız durum/hata güncellemesi (`saveAll` içinde) — "düzenlendi" SAYILMAZ, `touched` değişmez.
   */
  function setLineStatus(
    keys: string[],
    patch: Pick<DraftLine, 'status' | 'error'>,
  ): void {
    setLines((cur) =>
      cur
        ? cur.map((l) => (keys.includes(l.lineKey) ? { ...l, ...patch } : l))
        : cur,
    );
  }

  /** Kullanıcının GERÇEK bir düzenlemesi ya da onayı — `touched` her zaman `true` olur (U5). */
  function editLine(key: string, patch: Partial<DraftLine>): void {
    if (savingRef.current) return;
    setLines((cur) =>
      cur ? cur.map((l) => (l.lineKey === key ? { ...l, ...patch, touched: true } : l)) : cur,
    );
  }

  function removeLine(key: string): void {
    if (savingRef.current) return;
    setLines((cur) => (cur ? cur.filter((l) => l.lineKey !== key) : cur));
  }

  /** Elle eklenen boş kalem — kullanıcı zaten "bu kalem olacak" demiştir. */
  function addLine(): void {
    if (savingRef.current) return;
    setLines((cur) =>
      cur
        ? [
            ...cur,
            {
              rawName: '',
              productId: null,
              brandId: null,
              candidates: [],
              qtyFull: 1,
              qtyEmptyOut: 0,
              date: null,
              waybillNo: null,
              confidence: 'high',
              lineKey: crypto.randomUUID(),
              status: 'idle',
              touched: true,
            },
          ]
        : cur,
    );
  }

  function isLineValid(l: DraftLine): boolean {
    if (l.status === 'done') return true;
    if (!l.productId) return false;
    /**
     * KALEM KURALI (sunucuyla aynı): kalemde ya GELEN DOLU ya GİDEN BOŞ olmalı.
     *
     * `qtyFull` 0 olabilir — yalnız boş çıkış getiren kalem geçerlidir (fabrikadan boş alan
     * irsaliye). İkisi birlikte 0 ise kalem boştur ve kaydedilmez.
     */
    if (l.qtyFull + l.qtyEmptyOut < 1) return false;
    if (l.confidence === 'low' && !l.touched) return false;
    const product = productList.find((p) => p.id === l.productId);
    return !!product?.brandId;
  }

  /**
   * Kalemlerin markası: TEK marka olmalı (irsaliye markaya bağlı). Markası olmayan ürün ya da
   * birden fazla marka → kayıt yapılmaz.
   */
  const brandState = useMemo<'ok' | 'mismatch' | 'noBrand'>(() => {
    const ids = new Set<string>();
    for (const l of lines ?? []) {
      const product = productList.find((p) => p.id === l.productId);
      if (!product) continue;
      if (!product.brandId) return 'noBrand';
      ids.add(product.brandId);
    }
    if (ids.size > 1) return 'mismatch';
    return 'ok';
  }, [lines, productList]);

  const brandId = useMemo(() => {
    const ids = new Set<string>();
    for (const l of lines ?? []) {
      const product = productList.find((p) => p.id === l.productId);
      if (product?.brandId) ids.add(product.brandId);
    }
    const only = ids.size === 1 ? [...ids][0] : undefined;
    return only ?? null;
  }, [lines, productList]);

  /** Belgede birden fazla irsaliye numarası varsa kullanıcı uyarılır. */
  const distinctWaybills = useMemo(
    () => [
      ...new Set((lines ?? []).map((l) => l.waybillNo?.trim()).filter((w): w is string => !!w)),
    ],
    [lines],
  );

  /**
   * Aynı ürün iki kalemde olamaz (sunucu 400 döner; defterde tek satır olmalı). Panel kalemleri
   * BİRLEŞTİRMEZ — birleştirme adet toplamı demektir, hesap backend'de olur (kural 4). Kullanıcı
   * mükerrer kalemi siler.
   */
  const hasDuplicateProduct = useMemo(() => {
    const ids = new Set<string>();
    for (const l of lines ?? []) {
      if (!l.productId) continue;
      if (ids.has(l.productId)) return true;
      ids.add(l.productId);
    }
    return false;
  }, [lines]);

  const canSave =
    (lines ?? []).length > 0 &&
    waybillNo.trim().length > 0 &&
    brandState === 'ok' &&
    !hasDuplicateProduct &&
    (lines ?? []).every(isLineValid);
  const saving = savingLoop || (lines ?? []).some((l) => l.status === 'pending');
  const allSaved = (lines ?? []).length > 0 && (lines ?? []).every((l) => l.status === 'done');
  /**
   * K1a: en az bir kalem kaydedildiyse bu oturumda belge DEĞİŞTİRİLEMEZ. Yeniden "Oku" yeni
   * `clientId` üretir; kaydedilmiş irsaliye ikinci kez yazılıp stoğu şişirirdi.
   */
  const anySaved = (lines ?? []).some((l) => l.status === 'done');
  const docLocked = saving || anySaved;

  async function saveAll(): Promise<void> {
    if (savingRef.current || !linesRef.current) return;
    const rows = linesRef.current;
    if (rows.length === 0) return;
    const waybill = waybillRef.current.trim();
    if (!waybill) return;
    const targetBrand = brandId;
    if (!targetBrand) return;
    const payloadLines = rows
      .filter((l): l is DraftLine & { productId: string } => !!l.productId)
      .map((l) => ({ productId: l.productId, qty: l.qtyFull, qtyEmptyOut: l.qtyEmptyOut }));
    if (payloadLines.length === 0) return;

    savingRef.current = true;
    setSavingLoop(true);
    const keys = rows.map((l) => l.lineKey);
    setLineStatus(keys, { status: 'pending', error: undefined });
    try {
      // TEK çağrı: sunucu kalemleri TEK transaction'da yazar.
      await receive.mutateAsync({
        brandId: targetBrand,
        waybillNo: waybill,
        lines: payloadLines,
        note: docDateRef.current
          ? t('panel.stockIntake.noteDate', { date: formatDate(docDateRef.current) })
          : undefined,
        clientId: clientIdRef.current,
      });
      if (!unmountedRef.current) setLineStatus(keys, { status: 'done', error: undefined });
    } catch (err) {
      const message = (err as { message?: string } | null)?.message ?? t('error.internal');
      if (!unmountedRef.current) setLineStatus(keys, { status: 'error', error: message });
    } finally {
      savingRef.current = false;
      setSavingLoop(false);
    }
    await qc.invalidateQueries({ queryKey: ['stock-levels'] });
    await qc.invalidateQueries({ queryKey: ['stock-vehicles'] });
    await qc.invalidateQueries({ queryKey: ['stock-moves'] });
  }

  return (
    <div
      className="card"
      style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}
    >
      <strong style={{ fontSize: 15 }}>{t('stockIntake.modalTitle')}</strong>

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          className={tab === 'photo' ? 'btn btn-primary' : 'btn'}
          disabled={docLocked}
          onClick={() => setTab('photo')}
        >
          {t('stockIntake.tabPhoto')}
        </button>
        <button
          className={tab === 'paste' ? 'btn btn-primary' : 'btn'}
          disabled={docLocked}
          onClick={() => setTab('paste')}
        >
          {t('stockIntake.tabPaste')}
        </button>
      </div>

      {tab === 'photo' ? (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
          {t('stockIntake.filePrompt')}
          <input
            type="file"
            accept={ACCEPTED_TYPES}
            capture="environment"
            disabled={docLocked}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </label>
      ) : (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
          {t('stockIntake.pasteLabel')}
          <textarea
            value={pasteText}
            disabled={docLocked}
            onChange={(e) => setPasteText(e.target.value)}
            rows={6}
            placeholder={t('stockIntake.pasteHint')}
          />
        </label>
      )}

      {parse.isError ? (
        <p style={{ fontSize: 13, color: 'var(--danger)' }}>
          {(parse.error as { message?: string } | null)?.message ?? t('error.internal')}
        </p>
      ) : null}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button className="btn" disabled={saving} onClick={onDone}>
          {t('common.cancel')}
        </button>
        <button
          className="btn btn-primary"
          disabled={
            docLocked ||
            parse.isPending ||
            (tab === 'paste' ? pasteText.trim().length === 0 : !file)
          }
          onClick={() => void read()}
        >
          {parse.isPending ? t('stockIntake.reading') : t('stockIntake.readButton')}
        </button>
        {anySaved ? (
          <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
            {t('stockIntake.newDocHint')}
          </span>
        ) : null}
      </div>

      {lines ? (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <label
              style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, flex: 1 }}
            >
              {t('panel.stockIntake.waybillLabel')}
              <input
                data-testid="intake-waybill"
                value={waybillNo}
                disabled={docLocked}
                onChange={(e) => setWaybillNo(e.target.value)}
              />
            </label>
            <label
              style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, width: 180 }}
            >
              {t('panel.stockIntake.docDateLabel')}
              <input
                type="date"
                data-testid="intake-doc-date"
                value={docDate}
                disabled={docLocked}
                onChange={(e) => setDocDate(e.target.value)}
              />
            </label>
          </div>
          {waybillNo.trim().length === 0 ? (
            <p style={{ fontSize: 12.5, color: 'var(--warning)' }}>
              {t('panel.stockIntake.waybillRequired')}
            </p>
          ) : null}
          {distinctWaybills.length > 1 ? (
            <p style={{ fontSize: 12.5, color: 'var(--warning)' }}>
              {t('panel.stockIntake.multipleWaybills')}
            </p>
          ) : null}
          {brandState === 'mismatch' ? (
            <p style={{ fontSize: 12.5, color: 'var(--danger)' }}>
              {t('panel.stockIntake.brandMismatch')}
            </p>
          ) : null}
          {brandState === 'noBrand' ? (
            <p style={{ fontSize: 12.5, color: 'var(--danger)' }}>
              {t('panel.stockIntake.productWithoutBrand')}
            </p>
          ) : null}
          {hasDuplicateProduct ? (
            <p style={{ fontSize: 12.5, color: 'var(--danger)' }}>
              {t('stock.receive.duplicateProduct')}
            </p>
          ) : null}

          <DraftTable
            lines={lines}
            products={productList}
            saving={saving}
            onChange={editLine}
            onRemove={removeLine}
          />

          <div>
            <button className="btn" disabled={saving} onClick={addLine}>
              {t('panel.stockIntake.addLine')}
            </button>
          </div>
        </>
      ) : null}

      {warnings.length > 0 ? (
        <ul style={{ fontSize: 12.5, color: 'var(--warning)', paddingLeft: 18 }}>
          {warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      ) : null}

      {lines ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            className="btn btn-primary"
            disabled={!canSave || saving || allSaved}
            onClick={() => void saveAll()}
          >
            {saving ? t('stockIntake.saving') : t('stockIntake.saveButton', { n: lines.length })}
          </button>
          {allSaved ? (
            <span style={{ fontSize: 13, color: 'var(--success)' }}>
              {t('stockIntake.saveDone')}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function DraftTable({
  lines,
  products,
  saving,
  onChange,
  onRemove,
}: {
  lines: DraftLine[];
  products: Product[];
  /** Kayıt sürerken TÜM satırlar (bekleyenler dahil) kilitli. */
  saving: boolean;
  onChange: (key: string, patch: Partial<DraftLine>) => void;
  onRemove: (key: string) => void;
}) {
  if (lines.length === 0) {
    return (
      <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{t('stockIntake.noLines')}</p>
    );
  }
  return (
    <div className="card" style={{ overflow: 'auto' }}>
      <div
        className="table-head"
        style={{ padding: '0 14px', height: 40, display: 'flex', gap: 8 }}
      >
        <span style={{ flex: 2 }}>{t('stockIntake.colProduct')}</span>
        <span style={{ width: 70 }}>{t('panel.stockIntake.colQty')}</span>
        <span style={{ width: 70 }}>{t('panel.stockIntake.colEmptyOut')}</span>
        <span style={{ width: 70 }}>{t('stockIntake.colConfidence')}</span>
        <span style={{ width: 30 }} />
        <span style={{ width: 30 }} />
      </div>
      {lines.map((l) => {
        // K1: kaydedilmiş satırın alanları/silme kontrolü KAPALI — kaydedilmiş bir kaydın panelde
        // "düzenlenebilirmiş" gibi durmaması için. Kayıt sürerken HER satır kilitli; aksi
        // halde silme/düzenleme gönderilenle ekrandakini ayrıştırırdı.
        const locked = saving || l.status === 'done';
        const needsConfirm = l.confidence === 'low' && !l.touched && l.status !== 'done';
        /**
         * KALEM KURALI: dolu ve boş çıkış birlikte 0 ise satır boştur — kaydedilemez.
         * (Yalnız boş çıkışı olan satır GEÇERLİDİR: `qtyFull` 0 olabilir.)
         */
        const emptyLine = l.qtyFull + l.qtyEmptyOut < 1;
        return (
          <div key={l.lineKey}>
            <div
              data-testid="intake-row"
              style={{
                padding: '8px 14px',
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                borderTop: '1px solid var(--border)',
              }}
            >
              <select
                style={{ flex: 2 }}
                value={l.productId ?? ''}
                disabled={locked}
                onChange={(e) => onChange(l.lineKey, { productId: e.target.value || null })}
              >
                <option value="">{t('stockIntake.chooseProduct')}</option>
                {l.candidates.map((c) => (
                  <option key={c.productId} value={c.productId}>
                    {c.name} ({Math.round(c.score * 100)}%)
                  </option>
                ))}
                {products
                  .filter((p) => !l.candidates.some((c) => c.productId === p.id))
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
              </select>
              <input
                style={{ width: 70 }}
                inputMode="numeric"
                data-testid="intake-qty"
                disabled={locked}
                value={l.qtyFull}
                onChange={(e) =>
                  onChange(l.lineKey, { qtyFull: Number(e.target.value.replace(/\D/g, '') || 0) })
                }
              />
              {/**
               * BOŞ ÇIKIŞ (fabrikadan alınan boş). Kullanıcı adedi girer; panel hesap YAPMAZ,
               * adet olduğu gibi sunucuya gider (kural 4). Artık yok sayılmıyor: kalemin defter
               * satırına `FACTORY_OUT` olarak yazılır.
               */}
              <input
                style={{ width: 70 }}
                inputMode="numeric"
                data-testid="intake-empty-out"
                disabled={locked}
                value={l.qtyEmptyOut}
                onChange={(e) =>
                  onChange(l.lineKey, {
                    qtyEmptyOut: Number(e.target.value.replace(/\D/g, '') || 0),
                  })
                }
              />
              <span
                style={{ width: 70, fontSize: 12 }}
                className={l.confidence === 'low' ? 'badge-amber' : 'badge-blue'}
              >
                {l.confidence === 'low'
                  ? t('stockIntake.confidenceLow')
                  : t('stockIntake.confidenceHigh')}
              </span>
              <span style={{ width: 30, textAlign: 'center' }}>
                {l.status === 'done' ? '✓' : l.status === 'error' ? '✗' : null}
              </span>
              <button
                className="btn"
                style={{ padding: '4px 8px' }}
                disabled={locked}
                onClick={() => onRemove(l.lineKey)}
                aria-label={t('stockIntake.removeLine')}
              >
                ×
              </button>
            </div>
            {needsConfirm ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '0 14px 8px',
                  fontSize: 12,
                  color: 'var(--warning)',
                }}
              >
                <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input
                    type="checkbox"
                    checked={false}
                    disabled={locked}
                    onChange={() => onChange(l.lineKey, {})}
                  />
                  {t('stockIntake.confirmLow')}
                </label>
                <span>{t('stockIntake.confirmLowHint')}</span>
              </div>
            ) : null}
            {emptyLine ? (
              <p style={{ padding: '0 14px 8px', fontSize: 12.5, color: 'var(--warning)' }}>
                {t('panel.stockIntake.rowEmpty')}
              </p>
            ) : null}
            {l.error ? (
              <p style={{ padding: '0 14px 8px', fontSize: 12.5, color: 'var(--danger)' }}>
                {l.error}
              </p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
